// Weekly series charts for the drill-down. Weeks with missing data are shaded
// rather than charted as real drops — the sums only cover observed days.
import './SeriesChart.scss';
import { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import {
  Brand60,
  Neutral300,
  Neutral500,
  Neutral600,
  Success500,
  Warning600
} from '../../../../utils/colors';
import { DataQaSeriesGroup } from '../../dataQaContract';

const fmtCompact = (n: number): string => {
  if (!Number.isFinite(n)) {
    return '';
  }
  if (Math.abs(n) >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(1)}M`;
  }
  if (Math.abs(n) >= 1_000) {
    return `${(n / 1_000).toFixed(Math.abs(n) >= 100_000 ? 0 : 1)}k`;
  }
  return String(Math.round(n));
};

type BandKind = 'empty' | 'partial' | 'stale';

interface ShadeBand {
  start: number;
  end: number;
  kind: BandKind;
}

const BAND_FILL: Record<BandKind, { fill: string; opacity: number }> = {
  empty: { fill: Neutral600, opacity: 0.2 },
  partial: { fill: Neutral500, opacity: 0.12 },
  stale: { fill: Warning600, opacity: 0.18 }
};

const SeriesChart = ({
  label,
  weeks,
  values,
  bands,
  color,
  money
}: {
  label: string;
  weeks: string[];
  values: number[];
  /** Contiguous index runs (inclusive) of weeks with missing data. */
  bands: ShadeBand[];
  color: string;
  money?: boolean;
}) => {
  const prefix = money ? '$' : '';
  // A numeric index axis (not the week-string category axis) so the partial
  // shading can span half-slots on each side — a single partial week still
  // gets a visible band, which a zero-width category ReferenceArea would not.
  const data = useMemo(
    () => weeks.map((_, i) => ({ x: i, value: values[i] ?? null })),
    [weeks, values]
  );
  const tickStep = Math.max(1, Math.ceil(weeks.length / 6));
  const ticks = useMemo(() => data.map(d => d.x).filter(x => x % tickStep === 0), [data, tickStep]);
  const weekLabel = (x: unknown) => weeks[Math.round(Number(x))] ?? '';
  return (
    <div className='dq-chart'>
      <h4>{label}</h4>
      <ResponsiveContainer width='100%' height={110}>
        <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={Neutral300} vertical={false} />
          <XAxis
            dataKey='x'
            type='number'
            domain={[-0.5, weeks.length - 0.5]}
            ticks={ticks}
            tickFormatter={weekLabel}
            tick={{ fontSize: 10 }}
          />
          <YAxis
            tick={{ fontSize: 10 }}
            width={52}
            tickFormatter={(v: number) => `${prefix}${fmtCompact(v)}`}
          />
          <Tooltip
            labelFormatter={weekLabel}
            formatter={v => [`${prefix}${Number(v ?? 0).toLocaleString()}`, label]}
          />
          {bands.map(band => (
            <ReferenceArea
              key={`${band.kind}-${band.start}`}
              x1={band.start - 0.5}
              x2={band.end + 0.5}
              fill={BAND_FILL[band.kind].fill}
              fillOpacity={BAND_FILL[band.kind].opacity}
            />
          ))}
          <Line
            dataKey='value'
            stroke={color}
            dot={false}
            strokeWidth={1.5}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export const GroupSeriesCharts = ({
  sg,
  kpi,
  stale
}: {
  sg: DataQaSeriesGroup;
  kpi: string;
  stale: boolean;
}) => {
  const weeks = useMemo(() => sg.week_start.map(w => w.slice(0, 10)), [sg]);
  // Weeks with missing data get shaded rather than charted as real drops —
  // the sums only cover observed days. Fully-dark weeks (0 observed days)
  // shade darker than partial ones (1–6); a stale group's trailing
  // missing-data run is the "feed went quiet" window, tinted amber. Unknown
  // coverage (missing n_days_observed entry) counts as fully dark.
  const bands = useMemo(() => {
    const kindOf = (i: number): 'empty' | 'partial' | 'full' => {
      const n = sg.n_days_observed[i] ?? 0;
      if (n === 0) {
        return 'empty';
      }
      return n < 7 ? 'partial' : 'full';
    };
    const weekKinds = weeks.map((_, i) => kindOf(i));
    const out: ShadeBand[] = [];
    let runStart = 0;
    for (let i = 1; i <= weeks.length; i += 1) {
      if (i === weeks.length || weekKinds[i] !== weekKinds[runStart]) {
        const kind = weekKinds[runStart];
        if (kind !== 'full') {
          out.push({ start: runStart, end: i - 1, kind });
        }
        runStart = i;
      }
    }
    const last = out[out.length - 1];
    if (stale && last && last.end === weeks.length - 1) {
      last.kind = 'stale';
    }
    return out;
  }, [weeks, sg, stale]);

  // Columnar arrays are contractually row-aligned to week_start — bail to a
  // caption rather than chart misaligned data if a malformed file slips in.
  const aligned =
    sg.spend.length === weeks.length &&
    sg.volume.length === weeks.length &&
    sg.revenue.length === weeks.length &&
    sg.n_days_observed.length === weeks.length;
  if (!aligned) {
    return <p className='dq-chart-caption'>Time series unavailable for this group.</p>;
  }

  const kinds = new Set(bands.map(b => b.kind));
  const hasRevenue = sg.revenue.some(v => v != null && v !== 0);
  return (
    <div className='dq-charts' data-testid='dq-series-charts'>
      {bands.length > 0 && (
        <p className='dq-chart-legend'>
          {kinds.has('empty') && (
            <span>
              <i className='dq-sw dq-sw-empty' /> no data
            </span>
          )}
          {kinds.has('partial') && (
            <span>
              <i className='dq-sw dq-sw-partial' /> partial week
            </span>
          )}
          {kinds.has('stale') && (
            <span>
              <i className='dq-sw dq-sw-stale' /> feed quiet (stale)
            </span>
          )}
          <span className='dq-legend-note'>shaded weeks = the spend gaps at left</span>
        </p>
      )}
      <SeriesChart
        label='Weekly spend'
        weeks={weeks}
        values={sg.spend}
        bands={bands}
        color={Brand60}
        money
      />
      <SeriesChart
        label={`Weekly ${kpi}`}
        weeks={weeks}
        values={sg.volume}
        bands={bands}
        color={Warning600}
      />
      {hasRevenue && (
        <SeriesChart
          label='Weekly revenue'
          weeks={weeks}
          values={sg.revenue}
          bands={bands}
          color={Success500}
          money
        />
      )}
    </div>
  );
};
